/* eslint-disable @typescript-eslint/no-redeclare */
import {
  StockColorsEnum,
  StockDesignsEnum,
  StockEditionsEnum,
  StockSizeEnum,
  StockSurfacesEnum,
  StockTechnicalParametersEnum,
  StockThicknessEnum,
  StockTypesEnum
} from "../api";
import { colors, tileColors } from "../theme/colors";

export const StockFilters = {
  Type: "type",
  Discount: "discount",
  Discontinued: "discontinued",
  IndividualPrice: "individual",
  Preparing: "preparing",
  New: "new",
  Size: "size",
  Thickness: "thickness",
  Design: "design",
  Surface: "surface",
  Technical: "technical",
  Color: "color",
  Name: "name",
  Price: "price",
  Quantity: "quantity",
  InStock: "inStock"
};
export type StockFilters = (typeof StockFilters)[keyof typeof StockFilters];

export type SelectOption = {
  label: string;
  value: string;
};

export const sizeOptionsMap: { [key in StockSizeEnum]: string } = {
  [StockSizeEnum.Dimension7530]: "7,5x30",
  [StockSizeEnum.Dimension1590]: "15x90",
  [StockSizeEnum.Dimension2030]: "20x30",
  [StockSizeEnum.Dimension20120]: "20x120",
  [StockSizeEnum.Dimension2175]: "21x7,5",
  [StockSizeEnum.Dimension25575]: "25,5x7,5",
  [StockSizeEnum.Dimension3030]: "30x30",
  [StockSizeEnum.Dimension3060]: "30x60",
  [StockSizeEnum.Dimension30120]: "30x120",
  [StockSizeEnum.Dimension39898]: "39,8x9,8",
  [StockSizeEnum.Dimension408283]: "40,8x28,3",
  [StockSizeEnum.Dimension4040]: "40x40",
  [StockSizeEnum.Dimension4545]: "45x45",
  [StockSizeEnum.Dimension492145]: "49,2x14,5",
  [StockSizeEnum.Dimension5050]: "50x50",
  [StockSizeEnum.Dimension6060]: "60x60",
  [StockSizeEnum.Dimension60120]: "60x120",
  [StockSizeEnum.Dimension8080]: "80x80",
  [StockSizeEnum.Dimension120120]: "120x120",
  [StockSizeEnum.Dimension80160]: "80x160",
  [StockSizeEnum.Dimension100200]: "100x200"
};

export const thicknessOptionsMap: { [key in StockThicknessEnum]: string } = {
  [StockThicknessEnum.Thickness5Mm]: "5 mm",
  [StockThicknessEnum.Thickness55Mm]: "5,5 mm",
  [StockThicknessEnum.Thickness7Mm]: "7 mm",
  [StockThicknessEnum.Thickness75Mm]: "7,5 mm",
  [StockThicknessEnum.Thickness8Mm]: "8 mm",
  [StockThicknessEnum.Thickness85Mm]: "8,5 mm",
  [StockThicknessEnum.Thickness9Mm]: "9 mm",
  [StockThicknessEnum.Thickness10Mm]: "10 mm",
  [StockThicknessEnum.Thickness2Cm]: "20 mm",
  [StockThicknessEnum.Thickness30Mm]: "30 mm",
  [StockThicknessEnum.Thickness35Mm]: "35 mm"
};

export const colorMapping: { [key in StockColorsEnum]: string | undefined } = {
  [StockColorsEnum.Beige]: tileColors.beige,
  [StockColorsEnum.Brown]: tileColors.brown,
  [StockColorsEnum.Red]: tileColors.red,
  [StockColorsEnum.Green]: tileColors.green,
  [StockColorsEnum.Yellow]: tileColors.yellow,
  [StockColorsEnum.Blue]: tileColors.blue,
  [StockColorsEnum.White]: tileColors.white,
  [StockColorsEnum.Black]: tileColors.black,
  [StockColorsEnum.Gray]: tileColors.gray,
  [StockColorsEnum.Other]: undefined
};

export const colorFontMapping: {
  [key in StockColorsEnum]: string | undefined;
} = {
  [StockColorsEnum.Beige]: "black",
  [StockColorsEnum.Brown]: "white",
  [StockColorsEnum.Red]: "white",
  [StockColorsEnum.Green]: "black",
  [StockColorsEnum.Yellow]: "black",
  [StockColorsEnum.Blue]: "white",
  [StockColorsEnum.White]: "black",
  [StockColorsEnum.Black]: "white",
  [StockColorsEnum.Gray]: "black",
  [StockColorsEnum.Other]: "white"
};

export const editionColorMapping: { [key in StockEditionsEnum]: string } = {
  [StockEditionsEnum.New]: colors.green,
  [StockEditionsEnum.Preparing]: colors.capuccino,
  // [StockEditionsEnum.Recommended]: colors.yellow,
  [StockEditionsEnum.Discount]: colors.red,
  [StockEditionsEnum.Discontinued]: colors.yellow,
  [StockEditionsEnum.ClearanceSale]: colors.yellow
};

export const editionFontMapping: { [key in StockEditionsEnum]: string } = {
  [StockEditionsEnum.New]: "black",
  [StockEditionsEnum.Preparing]: "black",
  // [StockEditionsEnum.Recommended]: "black",
  [StockEditionsEnum.Discount]: "white",
  [StockEditionsEnum.Discontinued]: "black",
  [StockEditionsEnum.ClearanceSale]: "black"
};

export const sizeOptions = Object.entries(sizeOptionsMap).map(([key, value]) => ({
  label: value,
  value: key
}));

export const thicknessOptions = Object.entries(thicknessOptionsMap).map(([key, value]) => ({
  label: value,
  value: key
}));

type TFn = (key: string) => string;

export const typeOptions = (t: TFn): SelectOption[] =>
  Object.values(StockTypesEnum).map((value) => ({
    label: t(`attributes.type.${value}`),
    value: value
  }));

export const editionOptions = (t: TFn): SelectOption[] =>
  Object.values(StockEditionsEnum).map((value) => ({
    label: t(`attributes.edition.${value}`),
    value: value
  }));

export const technicalOptions = (t: TFn): SelectOption[] =>
  Object.values(StockTechnicalParametersEnum).map((value) => ({
    label: t(`attributes.technical.${value}`),
    value: value
  }));

export const surfaceOptions = (t: TFn): SelectOption[] =>
  Object.values(StockSurfacesEnum).map((value) => ({
    label: t(`attributes.surface.${value}`),
    value: value
  }));

export const designOptions = (t: TFn): SelectOption[] =>
  Object.values(StockDesignsEnum).map((value) => ({
    label: t(`attributes.design.${value}`),
    value: value
  }));

export const colorOptions = (t: TFn): SelectOption[] =>
  Object.values(StockColorsEnum).map((value) => ({
    label: t(`attributes.color.${value}`),
    value: value
  }));

export const priorityAttributes: Array<
  | StockTypesEnum
  | StockEditionsEnum
  | StockTechnicalParametersEnum
  | StockSurfacesEnum
  | StockDesignsEnum
  | StockColorsEnum
> = [
  StockTypesEnum.Flooring,
  StockTypesEnum.Tiling,
  StockTechnicalParametersEnum.FrostResistant,
  StockTechnicalParametersEnum.NonFrostResistant,
  StockSurfacesEnum.Matte,
  StockSurfacesEnum.Gloss,
  StockSurfacesEnum.HighGloss,
  StockTechnicalParametersEnum.ResistanceToSurfaceWearPei1,
  StockTechnicalParametersEnum.ResistanceToSurfaceWearPei2,
  StockTechnicalParametersEnum.ResistanceToSurfaceWearPei3,
  StockTechnicalParametersEnum.ResistanceToSurfaceWearPei4,
  StockTechnicalParametersEnum.AntiSlipR9,
  StockTechnicalParametersEnum.AntiSlipR10,
  StockTechnicalParametersEnum.AntiSlipR11
];

export const sortAttributeFunction = (
  a:
    | StockTypesEnum
    | StockEditionsEnum
    | StockTechnicalParametersEnum
    | StockSurfacesEnum
    | StockDesignsEnum
    | StockColorsEnum,
  b:
    | StockTypesEnum
    | StockEditionsEnum
    | StockTechnicalParametersEnum
    | StockSurfacesEnum
    | StockDesignsEnum
    | StockColorsEnum
) => {
  const indexOfA = priorityAttributes.indexOf(a);
  const indexOfB = priorityAttributes.indexOf(b);

  if (indexOfA > indexOfB) return 1;
  if (indexOfA < indexOfB) return -1;

  return 0;
};

export const getAttributeType = (
  attribute:
    | StockTypesEnum
    | StockEditionsEnum
    | StockTechnicalParametersEnum
    | StockSurfacesEnum
    | StockDesignsEnum
    | StockColorsEnum
) => {
  if (Object.values(StockTypesEnum).includes(attribute as StockTypesEnum)) {
    return StockFilters.Type;
  }

  if (attribute === StockEditionsEnum.Discount) {
    return StockFilters.Discount;
  }

  if (attribute === StockEditionsEnum.Preparing) {
    return StockFilters.Preparing;
  }

  if (attribute === StockEditionsEnum.New) {
    return StockFilters.New;
  }

  if (
    attribute === StockEditionsEnum.Discontinued ||
    attribute === StockEditionsEnum.ClearanceSale
  ) {
    return StockFilters.Discontinued;
  }

  if (
    Object.values(StockTechnicalParametersEnum).includes(attribute as StockTechnicalParametersEnum)
  ) {
    return StockFilters.Technical;
  }

  if (Object.values(StockSurfacesEnum).includes(attribute as StockSurfacesEnum)) {
    return StockFilters.Surface;
  }

  if (Object.values(StockDesignsEnum).includes(attribute as StockDesignsEnum)) {
    return StockFilters.Design;
  }

  if (Object.values(StockColorsEnum).includes(attribute as StockColorsEnum)) {
    return StockFilters.Color;
  }

  return;
};
